// App Store audit events in Axiom (PandaUnion docs/appstore-iap.md 审计日志,
// ts/services/appstore_audit.ts): dataset webhookdata, type 'appstore', the
// kind in `event`. Nested values arrive as JSON strings (*_json). Events that
// carry request_id share it with every event of the same HTTP request.
import { toAplString } from './apl.ts'

export const AUDIT_DATASET = 'webhookdata'

export interface AppStoreAuditEvent {
  _time: string
  _rowId: string
  type?: string
  event?: string
  source?: string | null
  request_id?: string | null
  notification_uuid?: string | null
  notification_type?: string | null
  subtype?: string | null
  environment?: string | null
  status?: string | null
  outcome?: string | null
  action?: string | null
  reason?: string | null
  user_id?: number | string | null
  service_id?: number | string | null
  payment_id?: number | string | null
  transaction_id?: string | null
  original_transaction_id?: string | null
  app_transaction_id?: string | null
  product_id?: string | null
  pay_type?: string | null
  months_added?: number | null
  months_removed?: number | null
  gb_granted?: number | null
  gb_removed?: number | null
  error?: string | null
  body_json?: string | null
  transaction_json?: string | null
  renewal_json?: string | null
  before_json?: string | null
  after_json?: string | null
  effects_json?: string | null
  detail_json?: string | null
}

export const AUDIT_EVENT_KINDS = [
  { value: 'notification_raw', label: '原始通知' },
  { value: 'notification', label: '解码通知' },
  { value: 'notification_result', label: '处理结果' },
  { value: 'transaction', label: '交易' },
  { value: 'refund', label: '退款' },
  { value: 'refund_reversal', label: '撤销退款' },
  { value: 'state', label: '订阅状态' },
  { value: 'pending_revocation', label: '待撤销' },
  { value: 'consumption_request', label: '退款询问' },
  { value: 'consumption_answer', label: '退款回复' },
  { value: 'release', label: '解绑' },
  { value: 'verify', label: '核对' },
  { value: 'test_notification', label: '测试通知' },
  { value: 'auto_buy_refused', label: '充值后自动购买被拒' },
] as const

const SOURCE_LABELS: Record<string, string> = {
  notification: 'Apple 通知',
  client_sync: '客户端同步',
  appstore_verify: '定时核对',
  renewal_daemon: '续费任务',
  consumption_daemon: '退款回复任务',
  admin: '管理员',
  payment_channel: '支付渠道',
}

export const auditEventLabel = (kind?: string | null) =>
  AUDIT_EVENT_KINDS.find(item => item.value === kind)?.label ?? kind ?? '—'
export const auditSourceLabel = (source?: string | null) =>
  (source && SOURCE_LABELS[source]) || source || '—'

export const AUDIT_RANGES = [
  { value: '1d', label: '24 小时', days: 1 },
  { value: '7d', label: '7 天', days: 7 },
  { value: '30d', label: '30 天', days: 30 },
  { value: '90d', label: '90 天', days: 90 },
] as const

export interface AuditFilters {
  // A key of AUDIT_RANGES, or 'custom' for from/to.
  range: string
  from: string
  to: string
  userId: string
  // Matches transaction_id or original_transaction_id.
  transactionId: string
  appTransactionId: string
  notificationUuid: string
  event: string
  problemsOnly: boolean
}

export const defaultAuditFilters = (): AuditFilters => ({
  range: '7d',
  from: '',
  to: '',
  userId: '',
  transactionId: '',
  appTransactionId: '',
  notificationUuid: '',
  event: '',
  problemsOnly: false,
})

const QUERY_KEYS = {
  userId: 'user_id',
  transactionId: 'transaction_id',
  appTransactionId: 'app_transaction_id',
  notificationUuid: 'notification_uuid',
  event: 'event',
} as const

const firstValue = (value: unknown) => {
  const item = Array.isArray(value) ? value[0] : value
  return typeof item === 'string' ? item.trim() : ''
}

export function auditFiltersFromQuery(query: Record<string, unknown>): AuditFilters {
  const filters = defaultAuditFilters()
  for (const [field, key] of Object.entries(QUERY_KEYS)) filters[field as keyof typeof QUERY_KEYS] = firstValue(query[key])
  filters.problemsOnly = firstValue(query.problems) === '1'
  filters.from = firstValue(query.from)
  filters.to = firstValue(query.to)
  const range = firstValue(query.range)
  if (filters.from || filters.to || range === 'custom') filters.range = 'custom'
  else if (AUDIT_RANGES.some(item => item.value === range)) filters.range = range
  return filters
}

// Only what differs from the defaults, so a plain tab link stays short.
export function auditFiltersToQuery(filters: AuditFilters): Record<string, string> {
  const query: Record<string, string> = {}
  if (filters.range !== defaultAuditFilters().range) query.range = filters.range
  if (filters.range === 'custom') {
    if (filters.from) query.from = filters.from
    if (filters.to) query.to = filters.to
  }
  for (const [field, key] of Object.entries(QUERY_KEYS)) {
    const value = filters[field as keyof typeof QUERY_KEYS].trim()
    if (value) query[key] = value
  }
  if (filters.problemsOnly) query.problems = '1'
  return query
}

export const AUDIT_QUERY_KEYS = ['range', 'from', 'to', ...Object.values(QUERY_KEYS), 'problems']

const MAX_RANGE_DAYS = 366

// The custom range inputs (datetime-local, no zone) are Beijing time, so the
// range does not depend on the admin's browser.
const parseRangeInput = (value: string) =>
  new Date(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(value) ? `${value}+08:00` : value)

// A datetime-local value for `date` in Beijing time.
export function beijingInputValue(date: Date): string {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(date).map(part => [part.type, part.value]),
  )
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`
}

export function validateAuditFilters(filters: AuditFilters): string | null {
  if (filters.userId.trim() && !/^\d+$/.test(filters.userId.trim())) return '用户 ID 只能是数字'
  if (filters.event && !AUDIT_EVENT_KINDS.some(item => item.value === filters.event)) return '未知的事件类型'
  if (filters.range === 'custom') {
    const from = parseRangeInput(filters.from)
    const to = parseRangeInput(filters.to)
    if (!filters.from || !filters.to || Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return '请选择开始和结束时间'
    if (to <= from) return '结束时间要晚于开始时间'
    if (to.getTime() - from.getTime() > MAX_RANGE_DAYS * 86_400_000) return `时间范围最长 ${MAX_RANGE_DAYS} 天`
  }
  return null
}

// Every query is bounded by this range.
export function auditTimeRange(filters: AuditFilters, now: Date): { startTime: string, endTime: string } {
  const invalid = validateAuditFilters(filters)
  if (invalid) throw new Error(invalid)
  if (filters.range === 'custom') {
    return { startTime: parseRangeInput(filters.from).toISOString(), endTime: parseRangeInput(filters.to).toISOString() }
  }
  const days = AUDIT_RANGES.find(item => item.value === filters.range)?.days ?? 7
  return { startTime: new Date(now.getTime() - days * 86_400_000).toISOString(), endTime: now.toISOString() }
}

export const auditFiltersNarrow = (filters: AuditFilters) =>
  Boolean(filters.userId.trim() || filters.transactionId.trim() || filters.appTransactionId.trim()
    || filters.notificationUuid.trim() || filters.event || filters.problemsOnly)

// Errors and rejections; keep in step with isAuditProblem.
const PROBLEM_OUTCOMES = ['error', 'retry', 'not_sent', 'failed']
const PROBLEM_STATUSES = ['failed', 'rejected', 'invalid', 'retry', 'error']
// webhookdata gets each App Store field only when the first event carrying it
// lands, and Axiom rejects a query that names a field the dataset lacks. So
// every field except type and _time is read through column_ifexists.
const col = (field: string) => `column_ifexists(${toAplString(field)}, "")`
// IDs are compared as strings: user_id is a number in App Store events, and
// other webhook types may store any of these fields with another type.
const colText = (field: string) => `tostring(${col(field)})`

const PROBLEM_APL = `isnotempty(${col('error')}) or ${col('outcome')} in (${PROBLEM_OUTCOMES.map(toAplString).join(', ')}) or ${col('outcome')} startswith "rejected" or ${col('status')} in (${PROBLEM_STATUSES.map(toAplString).join(', ')})`

export function isAuditProblem(event: AppStoreAuditEvent): boolean {
  const outcome = event.outcome ?? ''
  return Boolean(event.error)
    || PROBLEM_OUTCOMES.includes(outcome)
    || outcome.startsWith('rejected')
    || PROBLEM_STATUSES.includes(event.status ?? '')
}

const clampLimit = (limit: number, max: number) => Math.max(1, Math.min(Math.floor(Number(limit)) || 1, max))
const typeClause = `type == ${toAplString('appstore')}`

export function appStoreAuditApl(filters: AuditFilters, limit: number): string {
  const where = [typeClause]
  const userId = filters.userId.trim()
  const transactionId = filters.transactionId.trim()
  const appTransactionId = filters.appTransactionId.trim()
  const notificationUuid = filters.notificationUuid.trim()
  if (userId) where.push(`${colText('user_id')} == ${toAplString(userId)}`)
  if (transactionId) where.push(`${colText('transaction_id')} == ${toAplString(transactionId)} or ${colText('original_transaction_id')} == ${toAplString(transactionId)}`)
  if (appTransactionId) where.push(`${colText('app_transaction_id')} == ${toAplString(appTransactionId)}`)
  if (notificationUuid) where.push(`${colText('notification_uuid')} == ${toAplString(notificationUuid)}`)
  if (filters.event) where.push(`${col('event')} == ${toAplString(filters.event)}`)
  if (filters.problemsOnly) where.push(PROBLEM_APL)
  return [
    `['${AUDIT_DATASET}']`,
    ...where.map(clause => `| where ${clause}`),
    '| order by _time desc',
    `| limit ${clampLimit(limit, 500)}`,
  ].join('\n')
}

// IDs from older events can be numbers; Axiom will not compare a string
// column with an integer literal.
const aplList = (values: unknown[]) => `(${values.map(value => toAplString(String(value))).join(', ')})`

export const AUDIT_SIBLINGS_LIMIT = 1000
export const AUDIT_REQUESTS_LIMIT = 1000

// The rest of each push and request whose events a narrowing filter returned.
export function appStoreAuditSiblingsApl(uuids: string[], requestIds: string[]): string {
  const match = [
    ...(uuids.length ? [`${colText('notification_uuid')} in ${aplList(uuids)}`] : []),
    ...(requestIds.length ? [`${colText('request_id')} in ${aplList(requestIds)}`] : []),
  ]
  return [
    `['${AUDIT_DATASET}']`,
    `| where ${typeClause}`,
    `| where ${match.join(' or ')}`,
    '| order by _time desc',
    `| limit ${AUDIT_SIBLINGS_LIMIT}`,
  ].join('\n')
}

// Every event of those requests. Events written before verification (a
// retry, a missing migration) carry only their request ID.
export function appStoreAuditRequestsApl(requestIds: string[]): string {
  return [
    `['${AUDIT_DATASET}']`,
    `| where ${typeClause}`,
    `| where ${colText('request_id')} in ${aplList(requestIds)}`,
    '| order by _time desc',
    `| limit ${AUDIT_REQUESTS_LIMIT}`,
  ].join('\n')
}

export function auditEventsFromMatches(matches: Array<{ _time: string, _rowId: string, data: Record<string, unknown> }> | undefined): AppStoreAuditEvent[] {
  return (matches ?? []).map(match => ({ ...match.data, _time: match._time, _rowId: match._rowId }) as AppStoreAuditEvent)
}

// Axiom times have up to nine fraction digits; compare them at full precision.
const timeKey = (time: string) => {
  const match = /^(.*T\d{2}:\d{2}:\d{2})(?:\.(\d+))?Z$/.exec(time)
  if (!match) return new Date(time).toISOString().replace('Z', '000000')
  return `${match[1]}.${(match[2] ?? '').padEnd(9, '0').slice(0, 9)}`
}
const compareTime = (a: AppStoreAuditEvent, b: AppStoreAuditEvent) => {
  const ka = timeKey(a._time)
  const kb = timeKey(b._time)
  return ka < kb ? -1 : ka > kb ? 1 : 0
}

export function mergeAuditEvents(...lists: AppStoreAuditEvent[][]): AppStoreAuditEvent[] {
  const byRow = new Map<string, AppStoreAuditEvent>()
  for (const list of lists) for (const event of list) if (!byRow.has(event._rowId)) byRow.set(event._rowId, event)
  return [...byRow.values()].sort((a, b) => compareTime(b, a))
}

const decodeBase64Url = (text: string) => {
  const binary = atob(text.replace(/-/g, '+').replace(/_/g, '/'))
  return new TextDecoder().decode(Uint8Array.from(binary, char => char.charCodeAt(0)))
}

// Apple's signed payload, decoded without verifying it.
export function rawSignedPayload(event: AppStoreAuditEvent): Record<string, unknown> | null {
  if (event.event !== 'notification_raw' || !event.body_json) return null
  try {
    const signed = JSON.parse(event.body_json)?.signedPayload
    if (typeof signed !== 'string') return null
    const part = signed.split('.')[1]
    if (!part) return null
    const payload = JSON.parse(decodeBase64Url(part))
    return payload && typeof payload === 'object' ? payload : null
  } catch {
    // Anyone can post to the notification URL; a body that does not decode
    // simply has no UUID to group by.
    return null
  }
}

export function rawNotificationUuid(event: AppStoreAuditEvent): string | null {
  const uuid = rawSignedPayload(event)?.notificationUUID
  return typeof uuid === 'string' && uuid ? uuid : null
}

const idText = (value: unknown) => (value == null || value === '' ? null : String(value))

// Request IDs in `events` whose full event set has not been fetched.
export function unfetchedRequests(events: AppStoreAuditEvent[], fetched: Set<string>): string[] {
  return [...new Set(events
    .map(event => idText(event.request_id))
    .filter((id): id is string => id !== null && !fetched.has(id)))]
}

export interface AuditGroup {
  key: string
  // The push's notificationUUID; null for events outside a notification.
  uuid: string | null
  // False when the UUID is only what a raw body claims (not verified).
  uuidVerified: boolean
  source: string | null
  events: AppStoreAuditEvent[]
  latest: string
  notificationType: string | null
  subtype: string | null
  environment: string | null
  resultStatus: string | null
}

// Order of the phases of one request.
// Refund answers are written after the result of their request.
const STAGE: Record<string, number> = { notification_raw: 0, notification: 1, notification_result: 3, consumption_answer: 4 }
const stage = (event: AppStoreAuditEvent) => STAGE[event.event ?? ''] ?? 2
const compareRows = (a: AppStoreAuditEvent, b: AppStoreAuditEvent) => (a._rowId < b._rowId ? -1 : a._rowId > b._rowId ? 1 : 0)

// The emitter's per-process counter (detail.seq) and process (detail.pid).
// seq orders the events of one process; it means nothing across processes.
const emitOrder = (event: AppStoreAuditEvent): { pid: unknown, seq: number } | null => {
  try {
    const detail = JSON.parse(event.detail_json ?? 'null')
    return detail && typeof detail.seq === 'number' && detail.pid != null ? { pid: detail.pid, seq: detail.seq } : null
  } catch {
    // A detail cut at 64 KB does not parse; time and row order decide.
    return null
  }
}

// A push ends as processed if any delivery was; repeats that only found the
// first delivery (duplicate, in_flight) say least.
const resultStatus = (results: AppStoreAuditEvent[]) => {
  const statuses = results.map(event => event.status ?? '').filter(Boolean)
  if (statuses.includes('processed')) return 'processed'
  return statuses.filter(status => status !== 'duplicate' && status !== 'in_flight').at(-1) ?? statuses.at(-1) ?? null
}

// One HTTP request (request_id) is one group, and every delivery of one
// notificationUUID joins the same group. A raw body's UUID is a claim read
// without verification: it joins a push unless its own request failed
// verification (a forged body). Events without either (daemons, admins)
// stand alone.
export function groupAuditEvents(events: AppStoreAuditEvent[]): AuditGroup[] {
  const sorted = [...events].sort((a, b) => compareTime(a, b) || compareRows(a, b))
  const parent = sorted.map((_, index) => index)
  const find = (index: number): number => {
    while (parent[index] !== index) index = parent[index] = parent[parent[index]!]!
    return index
  }
  const refused = new Set(sorted
    .filter(event => event.event === 'notification' && event.status !== 'verified')
    .map(event => idText(event.request_id)))
  const owners = new Map<string, number>()
  sorted.forEach((event, index) => {
    const request = idText(event.request_id)
    const claimRefused = event.event === 'notification_raw' && request !== null && refused.has(request)
    const uuid = claimRefused ? null : idText(event.notification_uuid)
    for (const key of [request && `r:${request}`, uuid && `n:${uuid}`]) {
      if (!key) continue
      const owner = owners.get(key)
      if (owner === undefined) {
        owners.set(key, index)
        continue
      }
      const [ra, rb] = [find(index), find(owner)]
      // The earliest event names the group.
      if (ra !== rb) parent[Math.max(ra, rb)] = Math.min(ra, rb)
    }
  })

  const groups = new Map<number, AppStoreAuditEvent[]>()
  sorted.forEach((event, index) => {
    const root = find(index)
    const list = groups.get(root)
    if (list) list.push(event)
    else groups.set(root, [event])
  })
  return [...groups.entries()].map(([root, unordered]) => {
    // Requests in time order; within one request by phase, then time, where
    // the events of one process (detail.pid) follow their detail.seq: their
    // times are raised to never go back along seq. The key is total, so the
    // order does not depend on the input order.
    const requestStart = new Map<string, AppStoreAuditEvent>()
    for (const event of unordered) {
      const request = idText(event.request_id)
      if (request && !requestStart.has(request)) requestStart.set(request, event)
    }
    const startOf = (event: AppStoreAuditEvent) => requestStart.get(idText(event.request_id) ?? '') ?? event
    const orders = new Map(unordered.map(event => [event, emitOrder(event)]))
    const effective = new Map(unordered.map(event => [event, timeKey(event._time)]))
    const byProcess = new Map<string, AppStoreAuditEvent[]>()
    for (const event of unordered) {
      const order = orders.get(event)
      if (!order) continue
      const bucket = `${idText(event.request_id)}\u0000${String(order.pid)}`
      byProcess.set(bucket, [...(byProcess.get(bucket) ?? []), event])
    }
    for (const bucket of byProcess.values()) {
      let floor = ''
      for (const event of bucket.sort((a, b) => orders.get(a)!.seq - orders.get(b)!.seq || compareRows(a, b))) {
        const time = effective.get(event)!
        floor = time > floor ? time : floor
        effective.set(event, floor)
      }
    }
    const compareKey = (x: string, y: string) => (x < y ? -1 : x > y ? 1 : 0)
    const list = [...unordered].sort((a, b) => compareTime(startOf(a), startOf(b)) || compareRows(startOf(a), startOf(b))
      || stage(a) - stage(b)
      || compareKey(effective.get(a)!, effective.get(b)!)
      || compareKey(String(orders.get(a)?.pid ?? ''), String(orders.get(b)?.pid ?? ''))
      || Math.sign((orders.get(a)?.seq ?? Number.MAX_SAFE_INTEGER) - (orders.get(b)?.seq ?? Number.MAX_SAFE_INTEGER))
      || compareRows(a, b))
    const first = (pick: (event: AppStoreAuditEvent) => unknown, from = list) => {
      for (const event of from) {
        const value = pick(event)
        if (value != null && value !== '') return value
      }
      return null
    }
    // Raw bodies only claim what they carry; prefer what was verified.
    const verified = list.filter(event => event.event !== 'notification_raw')
    const raws = list.filter(event => event.event === 'notification_raw')
    const verifiedUuid = first(event => idText(event.notification_uuid), verified) as string | null
    const claimedUuid = first(event => idText(event.notification_uuid) ?? rawNotificationUuid(event), raws) as string | null
    const claimed = (pick: (event: AppStoreAuditEvent) => unknown) => (first(pick, verified) ?? first(pick, raws)) as string | null
    return {
      key: sorted[root]!._rowId,
      uuid: verifiedUuid ?? claimedUuid,
      uuidVerified: verifiedUuid !== null,
      source: first(event => event.source) as string | null,
      events: list,
      latest: unordered.at(-1)!._time,
      notificationType: claimed(event => event.notification_type),
      subtype: claimed(event => event.subtype),
      environment: claimed(event => event.environment),
      resultStatus: resultStatus(list.filter(event => event.event === 'notification_result')),
    }
  }).sort((a, b) => compareTime({ _time: b.latest } as AppStoreAuditEvent, { _time: a.latest } as AppStoreAuditEvent))
}

export interface ServiceChange { path: string, before: unknown, after: unknown }

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === 'object' && !Array.isArray(value)

const flatten = (value: unknown, prefix: string, out: Map<string, unknown>) => {
  if (!isPlainObject(value)) {
    out.set(prefix, value ?? null)
    return
  }
  for (const [key, item] of Object.entries(value)) flatten(item, prefix ? `${prefix}.${key}` : key, out)
}

// The service fields an event changed, from its before/after snapshots.
// Nested objects (extra.appstore as `appstore`) compare field by field.
export function serviceChanges(beforeJson: string | null | undefined, afterJson: string | null | undefined): ServiceChange[] {
  const before = beforeJson ? JSON.parse(beforeJson) : null
  const after = afterJson ? JSON.parse(afterJson) : null
  if (before == null && after == null) return []
  const beforeFields = new Map<string, unknown>()
  const afterFields = new Map<string, unknown>()
  if (before != null) flatten(before, '', beforeFields)
  if (after != null) flatten(after, '', afterFields)
  const paths = [...afterFields.keys(), ...[...beforeFields.keys()].filter(path => !afterFields.has(path))]
  const changes: ServiceChange[] = []
  for (const path of paths) {
    const was = beforeFields.get(path) ?? null
    const now = afterFields.get(path) ?? null
    if (JSON.stringify(was) !== JSON.stringify(now)) changes.push({ path, before: was, after: now })
  }
  return changes
}

const ISO_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/

// Beijing time, to the second.
export function auditTime(time: string | null | undefined): string {
  if (!time) return '—'
  // Shown as stored when it does not parse, rather than "Invalid Date" or a throw.
  if (Number.isNaN(Date.parse(time))) return time
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Shanghai',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date(time)).map(part => [part.type, part.value]),
  )
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`
}

// A diff value; null spelled out so "null → 2" cannot read as a dash.
const isTime = (value: string) => ISO_TIME.test(value) && !Number.isNaN(Date.parse(value))

export function formatAuditValue(value: unknown, withSeconds = false): string {
  if (value == null) return 'null'
  if (value === '') return '""'
  if (typeof value === 'string') return isTime(value) ? auditTime(value).slice(0, withSeconds ? 19 : 16) : value
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

// Times to the minute, or to the second when both fall in the same minute.
export function formatAuditChange(change: ServiceChange): { before: string, after: string } {
  const minuteBefore = formatAuditValue(change.before)
  const minuteAfter = formatAuditValue(change.after)
  const sameMinute = minuteBefore === minuteAfter && typeof change.before === 'string' && isTime(change.before)
  return sameMinute
    ? { before: formatAuditValue(change.before, true), after: formatAuditValue(change.after, true) }
    : { before: minuteBefore, after: minuteAfter }
}

const JSON_FIELDS = ['body_json', 'transaction_json', 'renewal_json', 'before_json', 'after_json', 'effects_json', 'detail_json'] as const

// The server's 64 KB cap per field (AUDIT_VALUE_MAX_BYTES).
const AUDIT_VALUE_MAX_BYTES = 64 * 1024

export interface AuditJsonSection {
  field: string
  text: string
  // Original size in bytes when the server cut the field.
  truncatedBytes: number | null
}

// Every JSON field of the event, pretty-printed; the raw body's signed
// payload is also shown decoded. Fields the server cut are marked.
export function auditJsonSections(event: AppStoreAuditEvent): AuditJsonSection[] {
  let detail: Record<string, any> = {}
  try {
    detail = JSON.parse(event.detail_json ?? 'null') ?? {}
  } catch {
    // A detail cut at 64 KB does not parse; it is shown as stored below.
  }
  const truncated: Record<string, unknown> = detail.truncated ?? {}
  const sections: AuditJsonSection[] = []
  for (const field of JSON_FIELDS) {
    const value = event[field]
    const cutBytes = typeof truncated[field] === 'number' ? truncated[field] as number : null
    if (value == null || value === '') {
      // Without the raw bytes, the server leaves an oversized body out.
      if (field === 'body_json' && event.event === 'notification_raw' && Number(detail.body_bytes) > AUDIT_VALUE_MAX_BYTES) {
        sections.push({ field, text: '', truncatedBytes: Number(detail.body_bytes) })
      }
      continue
    }
    let text = value
    try {
      text = JSON.stringify(JSON.parse(value), null, 2)
    } catch {
      // Shown as stored: cut at 64 KB, or not JSON.
    }
    sections.push({ field, text, truncatedBytes: cutBytes })
    if (field === 'body_json') {
      const payload = rawSignedPayload(event)
      if (payload) sections.push({ field: 'signedPayload', text: JSON.stringify(payload, null, 2), truncatedBytes: null })
    }
  }
  return sections
}

export const AUDIT_SCALAR_FIELDS = [
  'event', 'source', 'request_id', 'notification_uuid', 'notification_type', 'subtype', 'environment', 'status', 'outcome',
  'action', 'reason', 'user_id', 'service_id', 'payment_id', 'transaction_id', 'original_transaction_id',
  'app_transaction_id', 'product_id', 'pay_type', 'months_added', 'months_removed', 'gb_granted', 'gb_removed',
  'error',
] as const
